/**
 * Player progression: currencies, lifetime stats, achievements, cosmetics.
 * Fully data-driven — new achievements/cosmetics are registry entries, not UI
 * changes (FEATURE_MATRIX §8). Persisted locally (cloud sync lands Phase 6).
 *
 * Currencies (original names):
 * - Sparks  — normal-mode completion stars
 * - Lumens  — platformer completion stars
 * - Flux    — general-purpose orbs (earned by playing, spent in shops later)
 * - Prisms  — rare currency (dailies/quests, Phase 5b)
 */
import type { LevelData } from '../format/types';
import { mirrorKey } from './save-mirror';

export interface LifetimeStats {
  jumps: number;
  attempts: number;
  deaths: number;
  completions: number;
  platformerCompletions: number;
  practiceCompletions: number;
  playTimeS: number;
}

export interface Profile {
  version: 1;
  sparks: number;
  lumens: number;
  flux: number;
  prisms: number;
  /** Cosmetic ids owned (defaults are always owned implicitly). */
  unlocked: string[];
  equipped: {
    primary: string; // color id
    secondary: string; // color id
    core: string; // core glyph id
    trail: string; // trail id
    burst: string; // death-burst style id
    shell: string; // body shell (skin) id
  };
  /** Timed cache ("chest") claims. */
  lastCacheClaim: string | null;
  cacheClaims: number;
  /** Rotating contracts (quests): assignment date + per-entry baselines. */
  contracts: {
    date: string;
    entries: Array<{ id: string; baseline: number; claimed: boolean }>;
  } | null;
  /** Circuit (gauntlet) rewards already claimed. */
  claimedCircuits: string[];
  /** achievement id → ISO date earned. */
  achievements: Record<string, string>;
  /** "levelName:objectId" for permanently collected shards/marks. */
  shards: string[];
  stats: LifetimeStats;
}

export const DEFAULT_PROFILE: Profile = {
  version: 1,
  sparks: 0,
  lumens: 0,
  flux: 0,
  prisms: 0,
  unlocked: [],
  equipped: {
    primary: 'cyan',
    secondary: 'magenta',
    core: 'diamond',
    trail: 'streak',
    burst: 'ring',
    shell: 'solid',
  },
  lastCacheClaim: null,
  cacheClaims: 0,
  contracts: null,
  claimedCircuits: [],
  achievements: {},
  shards: [],
  stats: {
    jumps: 0,
    attempts: 0,
    deaths: 0,
    completions: 0,
    platformerCompletions: 0,
    practiceCompletions: 0,
    playTimeS: 0,
  },
};

// --- Cosmetics registry --------------------------------------------------------

export interface CosmeticDef {
  id: string;
  kind: 'color' | 'core' | 'trail' | 'burst' | 'shell';
  label: string;
  /** Hex for colors; style key for cores/trails/bursts. */
  value: string;
  /** Milestone unlock (absent = owned from the start unless priced). */
  requires?: { sparks?: number; flux?: number; achievement?: string };
  /** Shop price in Flux (purchasable; ownership recorded in profile.unlocked). */
  price?: number;
  /** Vault exclusive: only obtainable by claiming a vault tier. */
  vault?: boolean;
}

export const COSMETICS: readonly CosmeticDef[] = [
  { id: 'cyan', kind: 'color', label: 'Cyan', value: '#22d3ee' },
  { id: 'magenta', kind: 'color', label: 'Magenta', value: '#f0abfc' },
  { id: 'lime', kind: 'color', label: 'Lime', value: '#a3e635' },
  { id: 'amber', kind: 'color', label: 'Amber', value: '#fbbf24', requires: { sparks: 3 } },
  { id: 'rose', kind: 'color', label: 'Rose', value: '#fb7185', requires: { sparks: 6 } },
  { id: 'violet', kind: 'color', label: 'Violet', value: '#a78bfa', requires: { sparks: 10 } },
  { id: 'ice', kind: 'color', label: 'Ice', value: '#e0f2fe', requires: { flux: 100 } },
  { id: 'ember', kind: 'color', label: 'Ember', value: '#f97316', requires: { flux: 250 } },
  {
    id: 'void',
    kind: 'color',
    label: 'Void',
    value: '#312e81',
    requires: { achievement: 'shard-hunter' },
  },
  {
    id: 'aurora',
    kind: 'color',
    label: 'Aurora',
    value: '#34d399',
    requires: { achievement: 'all-clear' },
  },
  { id: 'diamond', kind: 'core', label: 'Diamond Core', value: 'diamond' },
  { id: 'ring', kind: 'core', label: 'Ring Core', value: 'ring', requires: { sparks: 4 } },
  { id: 'star', kind: 'core', label: 'Star Core', value: 'star', requires: { flux: 150 } },
  {
    id: 'bolt',
    kind: 'core',
    label: 'Bolt Core',
    value: 'bolt',
    requires: { achievement: 'century' },
  },
  { id: 'streak', kind: 'trail', label: 'Streak Trail', value: 'streak' },
  { id: 'none', kind: 'trail', label: 'No Trail', value: 'none' },
  { id: 'comet', kind: 'trail', label: 'Comet Trail', value: 'comet', requires: { sparks: 8 } },
  { id: 'embers', kind: 'trail', label: 'Ember Trail', value: 'embers', requires: { flux: 200 } },
  // Shop-only items (purchased with Flux via the Garage shop).
  { id: 'gold', kind: 'color', label: 'Gold', value: '#fde047', price: 500 },
  { id: 'plasma', kind: 'color', label: 'Plasma', value: '#e879f9', price: 350 },
  { id: 'eye', kind: 'core', label: 'Eye Core', value: 'eye', price: 400 },
  { id: 'ghost', kind: 'trail', label: 'Ghost Trail', value: 'ghost', price: 300 },
  // Death-burst styles.
  { id: 'ring', kind: 'burst', label: 'Ring Burst', value: 'ring' },
  {
    id: 'scatter',
    kind: 'burst',
    label: 'Scatter Burst',
    value: 'scatter',
    requires: { sparks: 5 },
  },
  { id: 'implode', kind: 'burst', label: 'Implosion', value: 'implode', price: 250 },
  // Body shells (skins): applied to every form's silhouette.
  { id: 'solid', kind: 'shell', label: 'Solid Shell', value: 'solid' },
  { id: 'hollow', kind: 'shell', label: 'Hollow Shell', value: 'hollow', requires: { sparks: 2 } },
  {
    id: 'striped',
    kind: 'shell',
    label: 'Striped Shell',
    value: 'striped',
    requires: { sparks: 7 },
  },
  { id: 'dotted', kind: 'shell', label: 'Dotted Shell', value: 'dotted', requires: { flux: 120 } },
  {
    id: 'circuit',
    kind: 'shell',
    label: 'Circuit Shell',
    value: 'circuit',
    requires: { flux: 300 },
  },
  {
    id: 'cracked',
    kind: 'shell',
    label: 'Cracked Shell',
    value: 'cracked',
    requires: { achievement: 'die-hard' },
  },
  { id: 'layered', kind: 'shell', label: 'Layered Shell', value: 'layered', price: 350 },
  { id: 'prism-shell', kind: 'shell', label: 'Prism Shell', value: 'prism', price: 600 },
  // Vault exclusives (claimed in the hidden Vault via shard tiers).
  { id: 'platinum', kind: 'color', label: 'Platinum', value: '#e2e8f0', vault: true },
  { id: 'prisma', kind: 'trail', label: 'Prisma Trail', value: 'prisma', vault: true },
  { id: 'nova', kind: 'burst', label: 'Nova Burst', value: 'nova', vault: true },
  { id: 'trine', kind: 'core', label: 'Trine Core', value: 'trine', vault: true },
];

/** Buy a priced cosmetic with Flux. Returns false if unaffordable/owned. */
export function purchaseCosmetic(profile: Profile, id: string): boolean {
  const def = COSMETICS.find((c) => c.id === id);
  if (!def || def.price === undefined) return false;
  if (profile.unlocked.includes(id)) return false;
  if (profile.flux < def.price) return false;
  profile.flux -= def.price;
  profile.unlocked.push(id);
  evaluateAchievements(profile);
  saveProfile(profile);
  return true;
}

export function cosmeticUnlocked(profile: Profile, def: CosmeticDef): boolean {
  if (profile.unlocked.includes(def.id)) return true;
  if (def.price !== undefined) return false; // shop items need purchase
  if (def.vault) return false; // vault exclusives need their tier claimed
  if (!def.requires) return true;
  const r = def.requires;
  if (r.sparks !== undefined && profile.sparks >= r.sparks) return true;
  if (r.flux !== undefined && profile.flux >= r.flux) return true;
  if (r.achievement !== undefined && profile.achievements[r.achievement] !== undefined) return true;
  return false;
}

// --- Achievements registry ------------------------------------------------------

export interface AchievementDef {
  id: string;
  label: string;
  desc: string;
  /** Pure check against the profile (called after every recorded event). */
  check(profile: Profile): boolean;
  /** Flux bonus granted once when earned. */
  rewardFlux: number;
}

export const ACHIEVEMENTS: readonly AchievementDef[] = [
  {
    id: 'first-clear',
    label: 'First Steps Taken',
    desc: 'Complete your first level.',
    check: (p) => p.stats.completions >= 1,
    rewardFlux: 25,
  },
  {
    id: 'warmed-up',
    label: 'Warmed Up',
    desc: 'Complete 3 levels.',
    check: (p) => p.stats.completions >= 3,
    rewardFlux: 50,
  },
  {
    id: 'all-clear',
    label: 'All Clear',
    desc: 'Complete 5 levels.',
    check: (p) => p.stats.completions >= 5,
    rewardFlux: 100,
  },
  {
    id: 'grounded',
    label: 'Grounded No More',
    desc: 'Jump 100 times.',
    check: (p) => p.stats.jumps >= 100,
    rewardFlux: 10,
  },
  {
    id: 'frequent-flyer',
    label: 'Frequent Flyer',
    desc: 'Jump 1,000 times.',
    check: (p) => p.stats.jumps >= 1000,
    rewardFlux: 40,
  },
  {
    id: 'century',
    label: 'Century',
    desc: 'Make 100 attempts.',
    check: (p) => p.stats.attempts >= 100,
    rewardFlux: 30,
  },
  {
    id: 'die-hard',
    label: 'Die Hard',
    desc: 'Die 50 times. It builds character.',
    check: (p) => p.stats.deaths >= 50,
    rewardFlux: 20,
  },
  {
    id: 'shard-curious',
    label: 'Shard Curious',
    desc: 'Collect your first secret shard.',
    check: (p) => p.shards.length >= 1,
    rewardFlux: 15,
  },
  {
    id: 'shard-hunter',
    label: 'Shard Hunter',
    desc: 'Collect 5 secret shards.',
    check: (p) => p.shards.length >= 5,
    rewardFlux: 75,
  },
  {
    id: 'vault-cracker',
    label: 'Vault Cracker',
    desc: 'Complete a platformer level.',
    check: (p) => p.stats.platformerCompletions >= 1,
    rewardFlux: 35,
  },
  {
    id: 'studied',
    label: 'Studied',
    desc: 'Complete a level in practice mode.',
    check: (p) => p.stats.practiceCompletions >= 1,
    rewardFlux: 10,
  },
  {
    id: 'sparkler',
    label: 'Sparkler',
    desc: 'Earn 10 Sparks.',
    check: (p) => p.sparks >= 10,
    rewardFlux: 50,
  },
  {
    id: 'marathon',
    label: 'Marathon',
    desc: 'Play for 30 minutes total.',
    check: (p) => p.stats.playTimeS >= 1800,
    rewardFlux: 25,
  },
];

// --- Persistence -----------------------------------------------------------------

const KEY = 'shape-shifters.profile.v1';
/** Last-good copy, promoted on every successful save (corruption guard). */
const KEY_BAK = `${KEY}.bak`;
/** Corrupt payloads are preserved here (never destroyed) for manual rescue. */
const KEY_CORRUPT = `${KEY}.corrupt`;

/** Parse + merge over defaults so new fields appear on old profiles.
 * Throws on anything that isn't a JSON object. */
function parseProfile(raw: string): Profile {
  const parsed = JSON.parse(raw) as Partial<Profile> | null;
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('profile payload is not an object');
  }
  return {
    ...structuredClone(DEFAULT_PROFILE),
    ...parsed,
    equipped: { ...DEFAULT_PROFILE.equipped, ...parsed.equipped },
    stats: { ...DEFAULT_PROFILE.stats, ...parsed.stats },
  };
}

export function loadProfile(): Profile {
  if (typeof localStorage === 'undefined') return structuredClone(DEFAULT_PROFILE);
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return structuredClone(DEFAULT_PROFILE);
    try {
      return parseProfile(raw);
    } catch (e) {
      // Corrupt main save: preserve the payload, then fall back to the
      // last-good backup instead of silently wiping progress.
      console.warn('Profile corrupt; payload preserved, trying backup.', e);
      try {
        localStorage.setItem(KEY_CORRUPT, raw);
      } catch {
        /* storage full — nothing more we can do */
      }
      const bak = localStorage.getItem(KEY_BAK);
      if (bak !== null) {
        const restored = parseProfile(bak); // backups only hold validated JSON
        console.warn('Profile restored from backup.');
        return restored;
      }
      return structuredClone(DEFAULT_PROFILE);
    }
  } catch (e) {
    console.warn('Profile load failed; starting fresh.', e);
    return structuredClone(DEFAULT_PROFILE);
  }
}

export function saveProfile(p: Profile): void {
  if (typeof localStorage === 'undefined') return; // headless tests
  try {
    // Promote the previous (known-parseable) save to backup before overwriting.
    const prev = localStorage.getItem(KEY);
    if (prev !== null) {
      try {
        JSON.parse(prev);
        localStorage.setItem(KEY_BAK, prev);
      } catch {
        /* never back up a corrupt payload */
      }
    }
    localStorage.setItem(KEY, JSON.stringify(p));
    mirrorKey(KEY);
  } catch (e) {
    console.warn('Profile save failed (storage unavailable?).', e);
  }
}

// --- Award pipeline ---------------------------------------------------------------

export interface CompletionAward {
  sparks: number;
  lumens: number;
  flux: number;
  newShards: number;
  achievements: AchievementDef[];
}

/** Evaluate achievement checks; returns newly earned defs (and applies rewards). */
export function evaluateAchievements(profile: Profile): AchievementDef[] {
  const earned: AchievementDef[] = [];
  for (const def of ACHIEVEMENTS) {
    if (profile.achievements[def.id] !== undefined) continue;
    if (def.check(profile)) {
      profile.achievements[def.id] = new Date().toISOString();
      profile.flux += def.rewardFlux;
      earned.push(def);
    }
  }
  return earned;
}

/** Record a completed run and grant currency. Practice completions only touch
 * stats. First-time level completion grants the difficulty as Sparks/Lumens. */
export function awardCompletion(
  profile: Profile,
  level: LevelData,
  opts: {
    jumps: number;
    timeS: number;
    practice: boolean;
    firstClear: boolean;
    collectedIds: number[];
  },
): CompletionAward {
  const award: CompletionAward = { sparks: 0, lumens: 0, flux: 0, newShards: 0, achievements: [] };
  profile.stats.playTimeS += opts.timeS;
  profile.stats.jumps += opts.jumps;
  if (opts.practice) {
    profile.stats.practiceCompletions += 1;
  } else {
    profile.stats.completions += 1;
    const isPlatformer = level.settings.mode === 'platformer';
    if (isPlatformer) profile.stats.platformerCompletions += 1;
    const difficulty = level.meta.difficulty ?? 1;
    if (opts.firstClear) {
      if (isPlatformer) {
        profile.lumens += difficulty;
        award.lumens = difficulty;
      } else {
        profile.sparks += difficulty;
        award.sparks = difficulty;
      }
    }
    award.flux = 10 + difficulty * 5;
    profile.flux += award.flux;
    // Shards/marks persist only on completed (non-practice) runs.
    for (const objId of opts.collectedIds) {
      const shardKey = `${level.meta.name}:${objId}`;
      if (!profile.shards.includes(shardKey)) {
        profile.shards.push(shardKey);
        award.newShards += 1;
      }
    }
  }
  award.achievements = evaluateAchievements(profile);
  saveProfile(profile);
  return award;
}

/** Record a death/attempt (called from the shell on respawn). */
export function recordDeath(profile: Profile, jumps: number, timeS: number): AchievementDef[] {
  profile.stats.deaths += 1;
  profile.stats.attempts += 1;
  profile.stats.jumps += jumps;
  profile.stats.playTimeS += timeS;
  const earned = evaluateAchievements(profile);
  saveProfile(profile);
  return earned;
}

// --- Timed caches ("chests") -----------------------------------------------------

/** Free cache cooldown (10 minutes of real time). */
export const CACHE_COOLDOWN_MS = 10 * 60 * 1000;

export function cacheStatus(
  profile: Profile,
  now = Date.now(),
): { ready: boolean; remainingMs: number } {
  if (!profile.lastCacheClaim) return { ready: true, remainingMs: 0 };
  const elapsed = now - Date.parse(profile.lastCacheClaim);
  return {
    ready: elapsed >= CACHE_COOLDOWN_MS,
    remainingMs: Math.max(0, CACHE_COOLDOWN_MS - elapsed),
  };
}

/** Claim the timed cache: Flux always, a Prism every third claim. */
export function claimCache(
  profile: Profile,
  now = Date.now(),
): { flux: number; prisms: number } | null {
  if (!cacheStatus(profile, now).ready) return null;
  profile.lastCacheClaim = new Date(now).toISOString();
  profile.cacheClaims += 1;
  const flux = 20 + (profile.cacheClaims % 5) * 10;
  const prisms = profile.cacheClaims % 3 === 0 ? 1 : 0;
  profile.flux += flux;
  profile.prisms += prisms;
  evaluateAchievements(profile);
  saveProfile(profile);
  return { flux, prisms };
}

// --- Contracts (rotating quests) --------------------------------------------------

export interface ContractDef {
  id: string;
  label: string;
  desc: string;
  target: number;
  rewardFlux: number;
  rewardPrisms: number;
  measure(profile: Profile): number;
}

export const CONTRACTS: readonly ContractDef[] = [
  {
    id: 'clear-2',
    label: 'Double Down',
    desc: 'Complete 2 levels',
    target: 2,
    rewardFlux: 40,
    rewardPrisms: 1,
    measure: (p) => p.stats.completions,
  },
  {
    id: 'jump-150',
    label: 'Leg Day',
    desc: 'Jump 150 times',
    target: 150,
    rewardFlux: 30,
    rewardPrisms: 0,
    measure: (p) => p.stats.jumps,
  },
  {
    id: 'attempt-20',
    label: 'Persistence',
    desc: 'Make 20 attempts',
    target: 20,
    rewardFlux: 25,
    rewardPrisms: 0,
    measure: (p) => p.stats.attempts,
  },
  {
    id: 'shard-1',
    label: 'Prospector',
    desc: 'Bank 1 new shard',
    target: 1,
    rewardFlux: 35,
    rewardPrisms: 1,
    measure: (p) => p.shards.length,
  },
  {
    id: 'die-10',
    label: 'No Fear',
    desc: 'Die 10 times',
    target: 10,
    rewardFlux: 20,
    rewardPrisms: 0,
    measure: (p) => p.stats.deaths,
  },
  {
    id: 'play-10m',
    label: 'Warm Circuits',
    desc: 'Play for 10 minutes',
    target: 600,
    rewardFlux: 30,
    rewardPrisms: 1,
    measure: (p) => Math.floor(p.stats.playTimeS),
  },
];

function dateSeed(date: string): number {
  let h = 2166136261;
  for (let i = 0; i < date.length; i++) {
    h ^= date.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

/** Ensure today's 3 contracts exist (rotates daily, seeded by the date). */
export function ensureContracts(profile: Profile, now = new Date()): void {
  const date = now.toISOString().slice(0, 10);
  if (profile.contracts?.date === date) return;
  const pool = [...CONTRACTS];
  let seed = dateSeed(date);
  const picks: ContractDef[] = [];
  for (let i = 0; i < 3 && pool.length > 0; i++) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    picks.push(pool.splice(seed % pool.length, 1)[0] as ContractDef);
  }
  profile.contracts = {
    date,
    entries: picks.map((c) => ({ id: c.id, baseline: c.measure(profile), claimed: false })),
  };
  saveProfile(profile);
}

export function contractProgress(
  profile: Profile,
  id: string,
): { done: number; target: number; claimed: boolean } | null {
  const entry = profile.contracts?.entries.find((e) => e.id === id);
  const def = CONTRACTS.find((c) => c.id === id);
  if (!entry || !def) return null;
  return {
    done: Math.min(def.target, Math.max(0, def.measure(profile) - entry.baseline)),
    target: def.target,
    claimed: entry.claimed,
  };
}

export function claimContract(
  profile: Profile,
  id: string,
): { flux: number; prisms: number } | null {
  const entry = profile.contracts?.entries.find((e) => e.id === id);
  const def = CONTRACTS.find((c) => c.id === id);
  if (!entry || !def || entry.claimed) return null;
  if (def.measure(profile) - entry.baseline < def.target) return null;
  entry.claimed = true;
  profile.flux += def.rewardFlux;
  profile.prisms += def.rewardPrisms;
  evaluateAchievements(profile);
  saveProfile(profile);
  return { flux: def.rewardFlux, prisms: def.rewardPrisms };
}

// --- Circuits (gauntlet-style level collections) ----------------------------------

import { getProgress } from './save';

export interface CircuitDef {
  id: string;
  name: string;
  desc: string;
  /** Level names (meta.name) that must each be completed at least once. */
  levels: string[];
  rewardFlux: number;
  rewardPrisms: number;
}

export const CIRCUITS: readonly CircuitDef[] = [
  {
    id: 'starter',
    name: 'Starter Circuit',
    desc: 'The fundamentals: run, shift, and ride the machinery.',
    levels: ['First Steps', 'Form Foundry', 'Pulse Works'],
    rewardFlux: 100,
    rewardPrisms: 2,
  },
  {
    id: 'machinist',
    name: 'Machinist Circuit',
    desc: 'Interactions under pressure: mirrors, duals, and raw speed.',
    levels: ['Twin Circuits', 'Overdrive'],
    rewardFlux: 150,
    rewardPrisms: 3,
  },
  {
    id: 'vault',
    name: 'Vault Circuit',
    desc: 'The platformer vaults: keys, locks, and patience.',
    levels: ['Switchback Vault', 'Under Grid', 'Sky Foundry'],
    rewardFlux: 150,
    rewardPrisms: 3,
  },
  {
    id: 'storm',
    name: 'Storm Circuit',
    desc: 'The end game: night shifts, signal storms, terminal speed.',
    levels: ['Nightshift', 'Signal Storm', 'Terminal Velocity'],
    rewardFlux: 300,
    rewardPrisms: 5,
  },
];

// --- The Vault (hidden shard-tier rewards) ----------------------------------------

export interface VaultTierDef {
  id: string;
  /** Total shards (across all levels) required to claim. */
  shards: number;
  /** Cosmetic id from COSMETICS granted on claim. */
  cosmetic: string;
  desc: string;
}

export const VAULT_TIERS: readonly VaultTierDef[] = [
  {
    id: 'vault-platinum',
    shards: 10,
    cosmetic: 'platinum',
    desc: 'A color forged from 10 shards.',
  },
  { id: 'vault-prisma', shards: 20, cosmetic: 'prisma', desc: 'A trail that cycles every hue.' },
  { id: 'vault-nova', shards: 30, cosmetic: 'nova', desc: 'Go out with a white-hot shockring.' },
  { id: 'vault-trine', shards: 45, cosmetic: 'trine', desc: 'The mark of a completed grid.' },
];

/** Claim a vault tier once enough shards are held. Returns the granted
 * cosmetic, or null if the tier is unknown, unearned, or already claimed. */
export function claimVaultTier(profile: Profile, id: string): CosmeticDef | null {
  const tier = VAULT_TIERS.find((v) => v.id === id);
  if (!tier) return null;
  if (profile.shards.length < tier.shards) return null;
  if (profile.unlocked.includes(tier.cosmetic)) return null;
  const def = COSMETICS.find((c) => c.id === tier.cosmetic && c.vault);
  if (!def) return null;
  profile.unlocked.push(def.id);
  evaluateAchievements(profile);
  saveProfile(profile);
  return def;
}

export function circuitProgress(def: CircuitDef): { done: number; total: number } {
  let done = 0;
  for (const name of def.levels) {
    if (getProgress(name).completions > 0) done++;
  }
  return { done, total: def.levels.length };
}

export function claimCircuit(
  profile: Profile,
  id: string,
): { flux: number; prisms: number } | null {
  const def = CIRCUITS.find((c) => c.id === id);
  if (!def || profile.claimedCircuits.includes(id)) return null;
  const prog = circuitProgress(def);
  if (prog.done < prog.total) return null;
  profile.claimedCircuits.push(id);
  profile.flux += def.rewardFlux;
  profile.prisms += def.rewardPrisms;
  evaluateAchievements(profile);
  saveProfile(profile);
  return { flux: def.rewardFlux, prisms: def.rewardPrisms };
}
